import { rem } from 'polished'
import React, { useCallback } from 'react'
import styled from 'styled-components'

import { DesktopLayoutSizes } from '../ItemCard/Common/utils'

import TextButton from 'components/Luxkit/Button/TextButton'
import LinePaperclipIcon from 'components/Luxkit/Icons/line/LinePaperclipIcon'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'
import TextLink from 'components/Luxkit/TextLink'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import Group from 'components/utils/Group'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import { useIsMobileScreen } from 'lib/web/deviceUtils'
import {
  useAttachDocument,
  useDetachDocument,
} from 'tripPlanner/hooks/api/documents'
import { TripDocumentWithItemInfo } from 'tripPlanner/types/documents'

const Root = styled.div`
  padding: ${rem(16)} ${rem(20)};
  border: 1px solid ${({ theme }) => theme.palette.neutral.default.five};
  border-radius: ${({ theme }) => theme.borderRadius.S};
`

const MainSection = styled.div`
  flex: 1;
`

const StyledLinePaperclipIcon = styled(LinePaperclipIcon)`
  align-self: flex-start;
`

const Updated = styled.span`
  display: none;
  ${DesktopLayoutSizes} {
    display: unset;
  }
`

const StyledStructureLabel = styled(StructureLabel)`
  height: 100%;
`

interface Props {
  document: TripDocumentWithItemInfo
  tripId: string
  tripItemId?: string
}

function MyDocumentsTile({ document, tripId, tripItemId }: Props) {
  const isAttached = !!document.item

  const { mutate: attachDocument, isLoading: attaching } = useAttachDocument({
    onSuccess: () => {
      showSnackbar('Document attached successfully', 'success')
    },
    onError: () => {
      showSnackbar('Failed to attach document', 'critical')
    },
    tripId,
  })
  const { mutate: detachDocument, isLoading: detaching } = useDetachDocument({
    onSuccess: () => {
      showSnackbar('Document detached successfully', 'success')
    },
    onError: () => {
      showSnackbar('Failed to detach document', 'critical')
    },
    tripId,
  })

  const handleAttach = useCallback(() => {
    if (tripItemId) {
      attachDocument({ tripId, tripItemId, filename: document.filename })
    }
  }, [attachDocument, document.filename, tripId, tripItemId])

  const handleDetach = useCallback(() => {
    if (tripItemId) {
      detachDocument({ tripId, tripItemId, filename: document.filename })
    }
  }, [detachDocument, document.filename, tripId, tripItemId])

  const isMobile = useIsMobileScreen()

  return (
    <Root>
      <Group direction="horizontal" gap={16} verticalAlign="end">
        <StyledLinePaperclipIcon size="M" />
        <MainSection>
          <Group direction="vertical" gap={12}>
            <div>
              <BodyText variant="large" wrap="letter-wrap">
                {document.filename}
              </BodyText>
              <Caption variant="medium" colour="neutral-three">
                <Updated>Updated&nbsp;</Updated>
                {document.lastModified.format(DMY_CASUAL_FORMAT)}
              </Caption>
            </div>
            {isAttached && (
              <div>
                <StyledStructureLabel variant="standard">
                  <BodyText
                    variant="small"
                    wrap="pre-wrap"
                    lineClamp={isMobile ? 2 : 1}
                  >
                    Attached to <TextLink>{document.item!.name}</TextLink>
                  </BodyText>
                </StyledStructureLabel>
              </div>
            )}
            {isMobile && (
              <>
                {isAttached && (
                  <div>
                    <TextButton onClick={handleDetach} kind="secondary">
                      Detach
                    </TextButton>
                  </div>
                )}
                {!isAttached && (
                  <div>
                    <TextButton onClick={handleAttach} kind="secondary">
                      Attach
                    </TextButton>
                  </div>
                )}
              </>
            )}
          </Group>
        </MainSection>
        {!isMobile && (
          <>
            {isAttached && (
              <TextButton
                onClick={handleDetach}
                disabled={detaching}
                kind="secondary"
              >
                Detach
              </TextButton>
            )}
            {!isAttached && (
              <TextButton
                onClick={handleAttach}
                disabled={attaching}
                kind="secondary"
              >
                Attach
              </TextButton>
            )}
          </>
        )}
      </Group>
    </Root>
  )
}

export default MyDocumentsTile
